import { HoverChar } from "@/components/ui/hover-char";
import { cn } from "@/utils/cn";
import { LEGAL_IDENTITY } from "@u/legalIdentity";
import { buildRoute } from "@u/routes";
import Logo from "@ui/logo";
import { ArrowUpRight, Mail } from "lucide-react";
import { conceptIcon } from "@u/conceptIcons";
import { siFacebook, siInstagram, siWhatsapp } from "simple-icons";

// LinkedIn fue retirada de simple-icons; glyph oficial con la misma shape
// { path, title } para que BrandGlyph la trate igual que las demás.
const siLinkedIn = {
  title: "LinkedIn",
  path: "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z",
};
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

interface FooterProps {
  className?: string;
}

/**
 * Brand glyph from simple-icons rendered with `currentColor` so it inherits the
 * footer's monochrome + hover treatment (instead of the solid brand-hex fill).
 */
function BrandGlyph({
  icon,
  className,
}: {
  icon: { path: string; title: string };
  className?: string;
}) {
  return (
    <svg
      role="img"
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d={icon.path} />
    </svg>
  );
}

/** Mono nav link with the sliding `_` char — the footer's own nav idiom
 *  (solutions / support / legal). `to` = in-app Link, `href` = mailto/external. */
function FooterNavLink({
  label,
  to,
  href,
}: {
  label: string;
  to?: string;
  href?: string;
}) {
  const cls =
    "group flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors";
  const inner = (
    <>
      <HoverChar
        char="_"
        className="group-hover:w-3 group-hover:opacity-100 font-mono text-primary text-xs"
      />
      <span className="transition-transform duration-300">{label}</span>
    </>
  );
  return to ? (
    <Link to={to} className={cls}>
      {inner}
    </Link>
  ) : (
    <a href={href} className={cls}>
      {inner}
    </a>
  );
}

/** Bordered contact pill with the fly-out `ArrowUpRight` — the footer's
 *  outbound-contact idiom (book demo / email / WhatsApp / GDPR). */
function ContactPill({
  icon,
  label,
  to,
  href,
}: {
  icon: ReactNode;
  label: string;
  to?: string;
  href?: string;
}) {
  const cls =
    "group inline-flex items-center gap-2 rounded-lg bg-muted/40 hover:bg-primary/5 border border-border/20 hover:border-primary/30 px-3 py-2 text-xs text-muted-foreground hover:text-primary transition-all duration-300";
  const inner = (
    <>
      {icon}
      <span>{label}</span>
      <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" />
    </>
  );
  return to ? (
    <Link to={to} className={cls}>
      {inner}
    </Link>
  ) : (
    <a
      href={href}
      target={href?.startsWith("mailto:") ? undefined : "_blank"}
      rel="noopener noreferrer"
      className={cls}
    >
      {inner}
    </a>
  );
}

export function Footer({ className }: FooterProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const currentYear = new Date().getFullYear();

  return (
    <footer
      className={cn(
        "border-t border-border/20 bg-background transition-colors duration-300 relative overflow-hidden",
        className,
      )}
    >
      {/* Sutil gradiente superior para separar el footer del contenido principal */}
      <div className="absolute top-0 inset-x-0 h-px bg-linear-to-r from-transparent via-primary/20 to-transparent" />

      <div className="mx-auto max-w-7xl px-6 pb-8 pt-16 lg:px-8 relative z-10">
        <div className="xl:grid xl:grid-cols-3 xl:gap-12">
          {/* Columna 1: Marca */}
          <div className="space-y-6">
            <div className="-ml-2 w-32 opacity-90 hover:opacity-100 transition-opacity">
              <Logo
                variant="horizontal"
                theme="auto"
                size="lg"
                className="w-full"
              />
            </div>

            {/* Redes Sociales - Botones táctiles */}
            <div className="flex gap-x-4">
              {[
                {
                  icon: siInstagram,
                  label: "Instagram",
                  href: "https://www.instagram.com/andarlabs/",
                },
                {
                  icon: siFacebook,
                  label: "Facebook",
                  href: "https://www.facebook.com/profile.php?id=61589050875546",
                },
                {
                  // LinkedIn ya no está en simple-icons — mismo path oficial
                  // que el fallback documentado de buttonCatalog.
                  icon: siLinkedIn,
                  label: "LinkedIn",
                  href: "https://www.linkedin.com/company/137354252/",
                },
              ].map(({ icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="group grid size-11 place-items-center text-muted-foreground hover:text-primary transition-colors"
                >
                  <BrandGlyph icon={icon} className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Columnas de Navegación - Con Hover Interactivo (Estilo Labs) */}
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 gap-8 xl:col-span-2 xl:mt-0">
            <div className="md:grid md:grid-cols-2 md:gap-8">
              <div>
                <span className="font-anton uppercase">
                  {t("nav_solutions")}
                </span>
                <ul className="mt-6 space-y-3">
                  {[
                    { label: t("portfolio"), to: buildRoute.portfolio(lang) },
                    { label: t("faq"), to: buildRoute.faq(lang) },
                  ].map(({ label, to }) => (
                    <li key={label}>
                      <FooterNavLink to={to} label={label} />
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-10 md:mt-0">
                <span className="font-anton uppercase">{t("support")}</span>
                <ul className="mt-6 space-y-3">
                  {[
                    {
                      label: t("stat_support"),
                      email: "support@andarlabs.com",
                    },
                    { label: t("sales"), email: "sales@andarlabs.com" },
                  ].map(({ label, email }) => (
                    <li key={email}>
                      <FooterNavLink href={`mailto:${email}`} label={label} />
                    </li>
                  ))}
                  <li className="pt-2">
                    <ContactPill
                      to={buildRoute.bookDemo(lang)}
                      icon={
                        <conceptIcon.bookDemo
                          className="h-3.5 w-3.5"
                          strokeWidth={1.5}
                        />
                      }
                      label={t("demo_book_cta")}
                    />
                  </li>
                  <li>
                    <ContactPill
                      href="mailto:hello@andarlabs.com"
                      icon={<Mail className="h-3.5 w-3.5" strokeWidth={1.5} />}
                      label={t("contact")}
                    />
                  </li>
                  <li>
                    <ContactPill
                      href="https://wa.me/351910117173"
                      icon={<BrandGlyph icon={siWhatsapp} className="h-3.5 w-3.5" />}
                      label="WhatsApp"
                    />
                  </li>
                </ul>
              </div>
            </div>

            <div>
              <span className="font-anton uppercase">{t("legal")}</span>
              <ul className="mt-6 space-y-3">
                {[
                  { label: t("privacy_label"), to: buildRoute.privacy(lang) },
                  { label: t("terms_label"), to: buildRoute.terms(lang) },
                ].map(({ label, to }) => (
                  <li key={label}>
                    <FooterNavLink to={to} label={label} />
                  </li>
                ))}
                <li className="pt-1">
                  <ContactPill
                    href="mailto:privacy@andarlabs.com"
                    icon={<Mail className="h-3.5 w-3.5" strokeWidth={1.5} />}
                    label={t("gdpr_privacy")}
                  />
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Barra de Copyright */}
        <div className="mt-16 border-t border-border/20 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-xs text-muted-foreground text-center md:text-left space-y-1">
            <p>
              &copy; {currentYear} ANDAR. {t("all_rights_reserved")}
            </p>
            {/* Identidad fiscal (DL 7/2004): marca ≠ sociedad — la línea lo
                dice sin esconderlo. El NIF aparece cuando se cargue en
                legalIdentity.ts; detalle completo en /terms y /privacy. */}
            <p>
              {t("footer_brand_line")}
              {LEGAL_IDENTITY.nif && ` — NIF ${LEGAL_IDENTITY.nif}`}
            </p>
          </div>

          <div className="group flex items-center gap-2">
            <div className="font-mono text-xs text-muted-foreground uppercase tracking-widest">
              {t("made_with_precision")}
            </div>
            <span className="text-xs text-muted-foreground/60">—</span>
            {/* crédito del estudio: texto en inglés a propósito (marca, no copy) */}
            <a
              href="https://slashtechconsulting.com"
              target="_blank"
              rel="noopener"
              className="font-mono text-xs text-muted-foreground uppercase tracking-widest hover:text-primary transition-colors"
            >
              Developed by SLASH
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
